import useIsVisible from "../hooks/useIsVisible";
import {
  forwardRef,
  MouseEvent,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
  useRef,
} from "react";
import {
  useArchiveEmails,
  useAssignEmailToInbox,
  useFetchEmailInbox,
  useMarkEmailsUnreadStatus,
  useListEmailFolders,
} from "services/email";
import { List, ListItem, Box, Button, Typography } from "@mui/material";
import { ReactComponent as EllipseIcon } from "../../icons/Menu/Ellipse.svg";
import WithDividers from "../helpers/WithDividers";
import Loading from "../common/Loading";
import { PlainMessage, protoInt64 } from "@bufbuild/protobuf";
import {
  FetchEmailInboxResponse_EmailMessageThread as EmailMessageThread,
  FetchEmailInboxRequest,
} from "protogen/email_service_pb";
import EmailCompose from "../email/EmailComposeDialog";
import { Family, FamilyStatus } from "protogen/advisors_service_pb";
import { useListAdvisorFamilies } from "services/advisor";
import { InboxHandle } from "./utils";
import DateDisplay from "../common/DateDisplay";
import RoundedDropDown from "../common/RoundedDropDown";
import useIsMobile from "../hooks/useIsMobile";
import {
  Mail,
  MailOpen,
  Trash2,
  ChevronDown,
  Paperclip,
  ShieldAlert,
  Bug,
} from "lucide-react";
import Checkbox from "../common/Checkbox";
import ConfirmationDialog, {
  useConfirmationDialog,
} from "../common/ConfirmationDialog";
import { CurrentUserContext } from "../context/RequireAuth";
import { isUserAdmin } from "common/userUtils";
import PopperMenu, {
  createMenuOption,
  SubmenuMenuOption,
} from "../common/PopperMenu";
import { useLocation } from "react-router-dom";
import { StatusContext } from "../context/StatusContextProvider";
import { StaticByline } from "components/activity/ScheduledMessageByline";
import EmailLabels from "../email/EmailLabels";
import { useWebSocketContext } from "components/context/SocketConsumerProvider";
import EmailInboxLabel from "../email/EmailInboxLabel";

type InboxSelectorProps = {
  thread: EmailMessageThread;
  families: Family[];
  onUpdate: () => void;
};
const InboxSelector = ({ thread, families, onUpdate }: InboxSelectorProps) => {
  const currentUser = useContext(CurrentUserContext);
  const { request, loading } = useAssignEmailToInbox(() => {
    onUpdate();
  });
  const onAssign = async (entityRef: string | null) => {
    if (!entityRef) return;
    if (entityRef === "my-inbox") {
      await request({
        emailRef: thread.latestEmailRef,
        inboxType: "advisor",
        inboxEntityRef: currentUser.ref,
      });
    } else {
      await request({
        emailRef: thread.latestEmailRef,
        inboxType: "family",
        inboxEntityRef: entityRef,
      });
    }
  };
  // All other emails at this time can be re-assigned.
  return (
    <RoundedDropDown
      initial={thread.family?.ref || null}
      // For now, family emails can't be re-assigned.
      disabled={!!thread.family || loading}
      onChange={async (s) => await onAssign(s?.value || null)}
      options={[
        ...families
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((f) => ({
            label: f.name,
            value: f.ref,
          })),
        {
          label: "My inbox",
          value: "my-inbox",
        },
      ]}
      displayValue={!thread.family ? "My inbox" : undefined}
      small={true}
    />
  );
};

const RISK_BADGES = [
  {
    topic: "Virus Risk",
    icon: Bug,
    bg: "#fef2f2",
    color: "#b91c1c",
    border: "#fca5a5",
  },
  {
    topic: "Spam Risk",
    icon: ShieldAlert,
    bg: "#fffbeb",
    color: "#b45309",
    border: "#fcd34d",
  },
] as const;

const ThreadRiskBadgeOrLabel = ({ thread }: { thread: EmailMessageThread }) => {
  const match = RISK_BADGES.find((b) => thread.topics.includes(b.topic));
  if (match) {
    const Icon = match.icon;
    return (
      <Box
        sx={{
          background: match.bg,
          color: match.color,
          border: `1px solid ${match.border}`,
          padding: "2px 6px",
          borderRadius: "4px",
          fontSize: "12px",
          height: "18px",
          lineHeight: "14px",
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        <Icon size={10} />
        {match.topic}
      </Box>
    );
  }
  return (
    <EmailLabels
      label={thread.folder}
      emailRef={thread.latestEmailRef}
      editable={false}
      variant="compact"
    />
  );
};

interface EmailListItemProps {
  thread: EmailMessageThread;
  families: Family[];
  onUpdate: () => void;
  selected?: boolean;
  onClick?: (latestEmailRef: string) => void;
  checked?: boolean;
  onCheck?: (checked: boolean) => void;
}

const EmailListItem = ({
  thread,
  families,
  onUpdate,
  onClick,
  selected = false,
  checked,
  onCheck,
}: EmailListItemProps) => {
  const isMobile = useIsMobile();
  const [markedRead, setMarkedRead] = useState(false);
  useEffect(() => {
    if (thread.unread && markedRead) {
      setMarkedRead(false);
    }
  }, [thread]);
  const unread = thread.unread && !markedRead;
  return (
    <ListItem
      onClick={() => {
        setMarkedRead(true);
        onClick && onClick(thread.latestEmailRef);
        checked !== undefined && onCheck?.(!checked);
      }}
      key={thread.latestEmailRef}
      sx={{
        cursor: "pointer",
        backgroundColor: selected ? "#F5F4F5" : "white",
        width: "100%",
        padding: "16px 20px",
        gap: "8px",
        alignItems: "start",
      }}
    >
      {checked !== undefined && (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="start"
          marginTop="3px"
        >
          <Checkbox
            checked={checked}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onCheck?.(e.target.checked);
            }}
            size={!isMobile ? "small" : undefined}
            sx={{ padding: 0 }}
          />
        </Box>
      )}
      <Box display="flex" flexDirection="column" gap="2px" width="100%">
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flex: "1 1px",
              overflow: "hidden",
              marginRight: "8px",
            }}
          >
            <Typography
              component="div"
              variant="bodySmall"
              color="text.tertiary"
            >
              {unread && (
                <EllipseIcon
                  height={8}
                  width={8}
                  style={{ marginRight: "5px" }}
                />
              )}
            </Typography>
            <Typography
              variant={unread ? "bodyHeavy" : "body"}
              color="text.primary"
              sx={{
                textOverflow: "ellipsis",
                textWrap: "nowrap",
                overflow: "hidden",
              }}
            >
              {/* TODO(Kip): This isn't to design, but it's going to take some work to wire up the senders on the backend. */}
              {thread.sender}
            </Typography>
          </Box>
          <DateDisplay
            date={new Date(Number(thread.lastMessageSec) * 1000)}
            color="text.secondary"
            sx={{ textAlign: "end" }}
            useRelativeAsPrimary={false}
          />
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          width="100%"
          alignItems="center"
          gap="8px"
        >
          <Typography
            component="div"
            variant={unread ? "bodyHeavy" : "body"}
            sx={{
              flexGrow: 1,
              textOverflow: "ellipsis",
              overflow: "hidden",
              minWidth: 0, // Allow shrinking
            }}
          >
            {/* handle scheduled emails */}
            {thread.latestIsDraft && !thread.firstScheduledDraftSec && (
              <Typography
                component="span"
                color="secondary.main"
                style={{
                  marginRight: "8px",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                Draft:
              </Typography>
            )}
            <EmailInboxLabel emailType={thread.emailType} />
            {thread.subject}
          </Typography>
          <Box display="flex" gap="4px" alignItems="center">
            {thread.hasAttachments && <Paperclip size={14} color="#616161" />}
            <ThreadRiskBadgeOrLabel thread={thread} />
          </Box>
        </Box>
        <Box>
          <Typography
            variant={unread ? "bodySmallHeavy" : "bodySmall"}
            sx={{
              color: "#616161",
              textOverflow: "ellipsis",
              overflow: "hidden",
            }}
          >
            {thread.teaserText}
          </Typography>
        </Box>

        {thread.firstScheduledDraftSec ? (
          <StaticByline scheduledTime={thread.firstScheduledDraftSec} />
        ) : null}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "8px",
          }}
        >
          <InboxSelector
            thread={thread}
            families={families}
            onUpdate={onUpdate}
          />
        </Box>
      </Box>
    </ListItem>
  );
};

const dedupeEmails = (
  a: EmailMessageThread[],
  b: EmailMessageThread[],
): EmailMessageThread[] => {
  const seen = new Set<string>();
  return b.concat(a).filter((thread) => {
    if (seen.has(thread.latestEmailRef)) {
      return false;
    }
    seen.add(thread.latestEmailRef);
    return true;
  });
};

const sortEntryLastUpdated = (a: EmailMessageThread, b: EmailMessageThread) => {
  const aTs = protoInt64.parse(a.lastMessageSec!);
  const bTs = protoInt64.parse(b.lastMessageSec!);
  return aTs < bTs ? 1 : aTs > bTs ? -1 : 0;
};

const InboxToolbar = ({
  markAsRead,
  checkedMessages,
  refresh,
  reset,
  setFilter,
  families,
  currentFilter,
}: {
  markAsRead: boolean;
  checkedMessages: null | Set<string>;
  refresh: () => void;
  reset: (empty: boolean) => void;
  setFilter: (s: EmailFilter) => void;
  families: Family[];
  currentFilter: EmailFilter;
}) => {
  const [multiSelect, setMultiSelect] = useState(false);
  const [foldersLoaded, setFoldersLoaded] = useState(false);
  const [availableFolders, setAvailableFolders] = useState<string[]>([]);
  const currentUser = useContext(CurrentUserContext);
  const isAdmin = isUserAdmin(currentUser);

  const confirmState = useConfirmationDialog();
  const [open, setOpen] = useState(false);
  const { refresh: statusRefresh } = useContext(StatusContext);

  // Lazy load folders when toolbar is opened for the first time
  const { request: listFoldersRequest, loading: listFoldersLoading } =
    useListEmailFolders((response) => {
      setAvailableFolders(response.folders || []);
      setFoldersLoaded(true);
    });

  const handleToggle = () => {
    if (!foldersLoaded && !listFoldersLoading && !open) {
      // Load folders when opening toolbar for the first time
      listFoldersRequest({});
    }
    setOpen((prevOpen) => !prevOpen);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const { request: unreadRequest, loading: unreadLoading } =
    useMarkEmailsUnreadStatus();
  const { request: archiveRequest, loading: archiveLoading } =
    useArchiveEmails();
  const loading = unreadLoading || archiveLoading;
  const changeReadStatus = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!loading && checkedMessages?.size) {
      await unreadRequest({
        emailRefs: Array.from(checkedMessages.values()),
        markAsRead: markAsRead,
      });
      refresh();
      statusRefresh();
    }
  };
  const archiveEmail = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!loading && checkedMessages?.size) {
      confirmState.openDialog(async () => {
        confirmState.setLoading(true);
        try {
          await archiveRequest({
            emailRefs: Array.from(checkedMessages.values()),
          });
          reset(false);
          refresh();
        } finally {
          confirmState.setLoading(false);
        }
      });
    }
  };
  const btnSx = {
    height: "unset",
    minWidth: "unset",
    ".MuiButton-startIcon": { marginLeft: 0, marginRight: "4px" },
  };

  const anchorRef = useRef<HTMLButtonElement>(null);
  const options = [
    createMenuOption(
      "Unread",
      () => {
        const f = {
          status: "unread",
          familyRef: currentFilter.familyRef,
          folders: currentFilter.folders,
          emailTypes: currentFilter.emailTypes,
        };
        setFilter(f);
        handleClose();
      },
      "mail",
    ),
    createMenuOption(
      "Archived",
      () => {
        const f = {
          status: "archived",
          familyRef: currentFilter.familyRef,
          folders: currentFilter.folders,
          emailTypes: currentFilter.emailTypes,
        };
        setFilter(f);
        handleClose();
      },
      "trash",
    ),
    createMenuOption(
      "Draft",
      () => {
        const f = {
          status: "draft",
          familyRef: currentFilter.familyRef,
          folders: currentFilter.folders,
          emailTypes: currentFilter.emailTypes,
        };
        setFilter(f);
        handleClose();
      },
      "pencil",
    ),
    createMenuOption(
      "Scheduled",
      () => {
        const f = {
          status: "scheduled",
          familyRef: currentFilter.familyRef,
          folders: currentFilter.folders,
          emailTypes: currentFilter.emailTypes,
        };
        setFilter(f);
        handleClose();
      },
      "scheduled",
    ),
    createMenuOption(
      "Sent",
      () => {
        const f = {
          status: "sent",
          familyRef: currentFilter.familyRef,
          folders: currentFilter.folders,
          emailTypes: currentFilter.emailTypes,
        };
        setFilter(f);
        handleClose();
      },
      "send",
    ),
    createMenuOption(
      "Spam",
      () => {
        const f = {
          status: "spam",
          familyRef: currentFilter.familyRef,
          folders: currentFilter.folders,
          emailTypes: currentFilter.emailTypes,
        };
        setFilter(f);
        handleClose();
      },
      "shieldOff",
    ),
    createMenuOption(
      "All",
      () => {
        const f = {
          status: "",
          familyRef: "",
          folders: [],
          emailTypes: [],
        };
        setFilter(f);
        handleClose();
      },
      "filterX",
    ),
  ];

  // Add Administrative filter for admins only
  if (isAdmin) {
    options.push(
      createMenuOption(
        "Administrative",
        () => {
          const f = {
            status: currentFilter.status,
            familyRef: currentFilter.familyRef,
            folders: currentFilter.folders,
            emailTypes: ["administrative"],
          };
          setFilter(f);
          handleClose();
        },
        "shield",
      ),
    );
  }

  if (families.length > 0) {
    options.push(
      <SubmenuMenuOption
        key="families"
        title="Family"
        onClick={(value) => {
          const f = {
            status: currentFilter.status,
            familyRef: value,
            folders: currentFilter.folders,
            emailTypes: currentFilter.emailTypes,
          };
          setFilter(f);
          handleClose();
        }}
        submenuOptions={families.map((f) => ({ label: f.name, value: f.ref }))}
        currentValue={currentFilter.familyRef || undefined}
      />,
    );
  }

  // Add folders submenu if folders are available
  options.push(
    <SubmenuMenuOption
      key="tags"
      title="Tags"
      disabled={availableFolders.length === 0}
      onClick={(value) => {
        const f = {
          status: currentFilter.status,
          familyRef: currentFilter.familyRef,
          folders: value ? [value] : [],
          emailTypes: currentFilter.emailTypes,
        };
        setFilter(f);
        handleClose();
      }}
      submenuOptions={availableFolders.map((folder) => ({
        label: folder,
        value: folder,
      }))}
      currentValue={
        currentFilter.folders.length > 0 ? currentFilter.folders[0] : undefined
      }
    />,
  );

  useEffect(() => {
    // Reset state!
    if (!multiSelect) {
      reset(true);
    } else {
      reset(false);
    }
  }, [multiSelect]);
  const filterTitle = () => {
    const family = currentFilter.familyRef
      ? families.find((f) => f.ref === currentFilter.familyRef)
      : undefined;
    const folderSuffix =
      currentFilter.folders.length > 0 ? `, ${currentFilter.folders[0]}` : "";
    const emailTypeSuffix =
      currentFilter.emailTypes.length > 0
        ? ` (${currentFilter.emailTypes[0]})`
        : "";
    if (currentFilter.status === "spam") {
      return "Spam";
    } else if (currentFilter.status && family) {
      return `Showing ${family.name} ${currentFilter.status}${folderSuffix}${emailTypeSuffix}`;
    } else if (currentFilter.status) {
      return `Showing ${currentFilter.status}${folderSuffix}${emailTypeSuffix}`;
    } else if (family) {
      return `Showing ${family.name}${folderSuffix}${emailTypeSuffix}`;
    } else if (currentFilter.folders.length > 0) {
      return `Showing ${currentFilter.folders[0]}${emailTypeSuffix}`;
    } else if (currentFilter.emailTypes.length > 0) {
      return `Showing ${currentFilter.emailTypes[0]}`;
    } else {
      return "Show all";
    }
  };
  return (
    <Box
      sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}
    >
      <Box
        display="flex"
        alignItems="center"
        flexDirection="row"
        justifyContent="space-between"
        sx={{
          padding: "0 20px",
          marginBottom: "8px",
        }}
      >
        {!multiSelect && (
          <Button
            sx={btnSx}
            variant="text"
            size="small"
            onClick={() => setMultiSelect(true)}
          >
            Select
          </Button>
        )}
        {multiSelect && (
          <Box display="flex" flexDirection="row" gap={"20px"}>
            <Button
              sx={btnSx}
              variant="text"
              size="small"
              onClick={() => setMultiSelect(false)}
            >
              {"Done"}
            </Button>
            <Button
              sx={btnSx}
              variant="text"
              startIcon={
                markAsRead ? <MailOpen size="16px" /> : <Mail size="16px" />
              }
              size="small"
              onClick={changeReadStatus}
              disabled={checkedMessages?.size === 0 || loading}
            >
              {markAsRead ? "Mark read" : "Mark unread"}
            </Button>
            <Button
              disabled={checkedMessages?.size === 0 || loading}
              sx={btnSx}
              variant="text"
              startIcon={<Trash2 size="16px" />}
              size="small"
              onClick={archiveEmail}
            >
              Archive
            </Button>
          </Box>
        )}
        {/*<Button*/}
        {/*  sx={btnSx}*/}
        {/*  variant="text"*/}
        {/*  endIcon={<ChevronDown size="16px" />}*/}
        {/*  size="small"*/}
        {/*>*/}
        {/*  Show all*/}
        {/*</Button>*/}
      </Box>
      <Box
        sx={{
          padding: "0 20px",
          marginBottom: "8px",
        }}
      >
        <Button
          variant="text"
          size="small"
          ref={anchorRef}
          onClick={handleToggle}
          sx={{ gap: "4px" }}
        >
          {filterTitle()} <ChevronDown size={20} color="#198282" />
        </Button>
        <PopperMenu
          placement="bottom"
          open={open}
          anchorRef={anchorRef}
          handleClose={() => {
            setOpen(false);
          }}
        >
          {options}
        </PopperMenu>
      </Box>
      <ConfirmationDialog
        title="Archive emails"
        noIcon={true}
        content="Are you sure you want to archive these emails?"
        {...confirmState.dialogProps}
      />
    </Box>
  );
};

type EmailFilter = {
  status: string;
  familyRef: string;
  folders: string[];
  emailTypes: string[];
};

interface EmailInboxProps {
  create?: boolean;
  selectedRef?: string;
  onEntrySelect(latestEmailRef: string): void;
  onFilterChange?: (isSpamView: boolean) => void;
}

export default forwardRef<InboxHandle, EmailInboxProps>(
  (
    {
      create = false,
      selectedRef,
      onEntrySelect,
      onFilterChange,
    }: EmailInboxProps,
    ref,
  ) => {
    const { isVisible } = useIsVisible({});
    const [checkedMessages, setCheckedMessages] = useState<null | Set<string>>(
      null,
    );
    const { registerHandler, unregisterHandler } = useWebSocketContext();
    const [emailComposeOpen, setEmailComposeOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [feedCursor, setFeedCursor] = useState<string | null>(null);
    const [threads, setThreads] = useState<EmailMessageThread[]>([]);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const folder = queryParams.get("folder");
    const [emailFilter, setEmailFilter] = useState<EmailFilter>({
      status: "",
      familyRef: "",
      folders: folder ? [folder] : [],
      emailTypes: [],
    });

    // Handle /inbox/email/create?email=foo@bar.com&emailSubject=...&emailBody=...
    const initialToAddress = queryParams.get("email") || "";
    const initialSubject = queryParams.get("emailSubject") || "";
    const initialBody = queryParams.get("emailBody") || "";
    useEffect(() => {
      if (create) {
        setEmailComposeOpen(true);
      }
    }, [create]);

    useImperativeHandle(ref, () => ({
      triggerCompose: () => setEmailComposeOpen(true),
      triggerRefresh: () => request({ ...emailFilter, cursor: "" }),
    }));
    const { request: emailRequest, loading } = useFetchEmailInbox();
    const { request: familyRequest, data: familyData } =
      useListAdvisorFamilies();

    const request = async (req: PlainMessage<FetchEmailInboxRequest>) => {
      const firstPage = !req.cursor;
      const r = await emailRequest(req);
      if (!r) return;
      setLoaded(true);

      setThreads((t) =>
        // When refreshing the first page do a hard refresh
        dedupeEmails(firstPage ? [] : t, r.threads).sort(sortEntryLastUpdated),
      );
      if (r.threads.length === 0 || !r.nextCursor) {
        setHasMore(false);
      }
      setFeedCursor(r.nextCursor);
      if (checkedMessages && checkedMessages.size > 0) {
        const emailRefs = new Set(r.threads.map((t) => t.latestEmailRef));
        setCheckedMessages((s) => {
          if (!s) return null;
          return new Set(Array.from(s).filter((ref) => emailRefs.has(ref)));
        });
      }
    };

    useEffect(() => {
      request({ ...emailFilter, cursor: "", folders: folder ? [folder] : [] });
      familyRequest();
    }, [queryParams.get("folder")]);

    useEffect(() => {
      if (
        !(
          emailFilter.status ||
          emailFilter.familyRef ||
          emailFilter.folders.length > 0
        )
      ) {
        const pushHandler = () => {
          // Stop refreshing once we've loaded further pages. Just too hard to manage that state.
          if (isVisible && page === 0) {
            request({
              status: "",
              familyRef: "",
              cursor: "",
              folders: [],
              emailTypes: [],
            });
          }
        };
        registerHandler("email/", pushHandler);
        return () => {
          unregisterHandler("email/", pushHandler);
        };
      }
    }, [page, emailFilter]);

    const nextPage = async () => {
      if (feedCursor) {
        setPage((p) => p + 1);
        await request({
          ...emailFilter,
          cursor: feedCursor,
        });
      }
    };

    if (!loaded) return <Loading />;
    const invalidFamilyStatuses = new Set([
      FamilyStatus.DEACTIVATED,
      FamilyStatus.DIGITAL_DEACTIVATED,
      FamilyStatus.DIGITAL_DEACTIVATED_TRIAL,
      FamilyStatus.DEACTIVATED_PROSPECT,
    ]);
    return (
      <>
        <InboxToolbar
          families={familyData?.families || []}
          markAsRead={threads.some(
            (t) => checkedMessages?.has(t.latestEmailRef) && t.unread,
          )}
          reset={(empty) =>
            empty
              ? setCheckedMessages(null)
              : setCheckedMessages(new Set(selectedRef ? [selectedRef] : []))
          }
          checkedMessages={checkedMessages}
          refresh={() =>
            request({
              ...emailFilter,
              // Don't set a cursor since we are refreshing a past page
              cursor: "",
            })
          }
          setFilter={async (filter) => {
            setEmailFilter(filter);
            onFilterChange?.(filter.status === "spam");
            await request({
              ...filter,
              // Don't set a cursor since we are refreshing a past page
              cursor: "",
            });
          }}
          currentFilter={emailFilter}
        />
        <Box
          sx={{
            overflowY: "auto",
            borderTop: "1px solid #E2E3E4",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <List
            sx={{
              paddingTop: 0,
              "&::-webkit-scrollbar": {
                display: "none",
              },
            }}
          >
            <WithDividers>
              {threads.map((thread) => (
                <EmailListItem
                  checked={checkedMessages?.has(thread.latestEmailRef)}
                  onCheck={(checked) => {
                    if (checked) {
                      setCheckedMessages((s) => {
                        s?.add(thread.latestEmailRef);
                        return new Set(s);
                      });
                    } else {
                      setCheckedMessages((s) => {
                        s?.delete(thread.latestEmailRef);
                        return new Set(s);
                      });
                    }
                  }}
                  key={thread.latestEmailRef}
                  thread={thread}
                  families={(familyData?.families || []).filter(
                    (f) => !invalidFamilyStatuses.has(f.status),
                  )}
                  onUpdate={() => request({ ...emailFilter, cursor: "" })}
                  onClick={onEntrySelect}
                  selected={
                    thread.latestEmailRef === selectedRef ||
                    checkedMessages?.has(thread.latestEmailRef)
                  }
                />
              ))}
            </WithDividers>
            {loaded && threads.length >= 0 && (
              <Box display="flex" justifyContent="center" alignItems="center">
                <Button
                  sx={{ marginTop: "4px", marginBottom: "20px" }}
                  variant="outlined"
                  disabled={loading || !hasMore}
                  onClick={nextPage}
                >
                  Load more
                </Button>
              </Box>
            )}
          </List>
          <EmailCompose
            closed={!emailComposeOpen}
            onClose={() => {
              setEmailComposeOpen(false);
              // Refresh the inbox after creating a draft.
              request({ ...emailFilter, cursor: "" });
            }}
            onSent={(e) => {
              onEntrySelect(e.emailMessage?.ref || "");
            }}
            onRemove={() => {
              request({ ...emailFilter, cursor: "" });
            }}
            initialTo={initialToAddress ? [initialToAddress] : []}
            initialSubject={initialSubject}
            initialContent={initialBody}
            // TODO: Handle replies...
            // inReplyTo={feedFocusState.replyToEmail}
            // TODO: Handle drafts...
            // initialDraft={feedFocusState.editEmailDraft}
          />
        </Box>
      </>
    );
  },
);
